import { Check, Copy01 } from "@untitledui/icons";
import { useCallback, useEffect, useState } from "react";
import type { Placement } from "react-aria";
import { Button as AriaButton, type ButtonProps } from "react-aria-components";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

export type CopyIconPosition = "left" | "right";
export type TooltipPlacement =
	| "top"
	| "bottom"
	| "left"
	| "right"
	| "top-start"
	| "top-end"
	| "bottom-start"
	| "bottom-end"
	| "left-start"
	| "left-end"
	| "right-start"
	| "right-end";

export interface CopyToClipboardProps extends Omit<ButtonProps, "children"> {
	/**
	 * Text to be copied to clipboard
	 */
	text: string;
	/**
	 * Visual content to display (optional, defaults to text prop)
	 */
	children?: React.ReactNode;
	/**
	 * Position of the copy icon
	 * @default "right"
	 */
	iconPosition?: CopyIconPosition;
	/**
	 * Tooltip placement relative to the trigger
	 * @default "top"
	 */
	tooltipPlacement?: TooltipPlacement;
	/**
	 * Duration in milliseconds to show the "Copied!" feedback
	 * @default 2000
	 */
	feedbackDuration?: number;
	/**
	 * Custom class name for the wrapper
	 */
	className?: string;
	/**
	 * Custom class name for the text content
	 */
	textClassName?: string;
	/**
	 * Custom class name for the icon button
	 */
	iconClassName?: string;
	/**
	 * Size of the copy icon
	 * @default 16
	 */
	iconSize?: number;
	/**
	 * Callback when copy is successful
	 */
	onCopySuccess?: () => void;
	/**
	 * Callback when copy fails
	 */
	onCopyError?: (error: Error) => void;
}

/**
 * CopyToClipboard component
 *
 * A reusable component that wraps text content and provides a copy-to-clipboard button.
 * Features:
 * - Visual feedback via tooltip ("Copy" → "Copied!")
 * - Icon changes from Copy to Check on success
 * - Configurable icon position and tooltip placement
 * - Keyboard accessible (Button element)
 * - TypeScript support with full type safety
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CopyToClipboard text="Hello World">
 *   Hello World
 * </CopyToClipboard>
 *
 * // With custom placement
 * <CopyToClipboard
 *   text="user-id-123"
 *   tooltipPlacement="bottom"
 *   iconPosition="left"
 * >
 *   user-id-123
 * </CopyToClipboard>
 *
 * // Display text different from copied text
 * <CopyToClipboard text="full-long-value">
 *   Shortened display
 * </CopyToClipboard>
 * ```
 */
export function CopyToClipboard({
	text,
	children,
	iconPosition = "right",
	tooltipPlacement = "top",
	feedbackDuration = 2000,
	className,
	textClassName,
	iconClassName,
	iconSize = 16,
	onCopySuccess,
	onCopyError,
	...buttonProps
}: CopyToClipboardProps) {
	const [isCopied, setIsCopied] = useState(false);

	const handleCopy = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			event.preventDefault();
			try {
				await navigator.clipboard.writeText(text);
				setIsCopied(true);
				onCopySuccess?.();
			} catch (error) {
				console.error("Failed to copy text:", error);
				onCopyError?.(error as Error);
			}
		},
		[text, onCopySuccess, onCopyError],
	);

	// Reset the copied state after the feedback duration
	useEffect(() => {
		if (isCopied) {
			const timer = setTimeout(() => {
				setIsCopied(false);
			}, feedbackDuration);

			return () => clearTimeout(timer);
		}
	}, [isCopied, feedbackDuration]);

	const displayContent = children || text;
	const tooltipContent = isCopied ? "Copied!" : "Copy";
	const Icon = isCopied ? Check : Copy01;

	return (
		<div className={cx("inline-flex items-center gap-2", className)}>
			{iconPosition === "left" && (
				<Tooltip
					title={tooltipContent}
					placement={tooltipPlacement as Placement}
				>
					<AriaButton
						{...buttonProps}
						onClick={(e) =>
							handleCopy(e as unknown as React.MouseEvent<HTMLButtonElement>)
						}
						className={cx(
							"inline-flex items-center justify-center rounded p-1 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2",
							iconClassName,
						)}
						aria-label={tooltipContent}
					>
						<Icon
							size={iconSize}
							className={cx(
								"transition-colors",
								isCopied ? "text-success-600" : "text-gray-400",
							)}
						/>
					</AriaButton>
				</Tooltip>
			)}

			<span className={cx("select-all", textClassName)}>{displayContent}</span>

			{iconPosition === "right" && (
				<Tooltip
					title={tooltipContent}
					placement={tooltipPlacement as Placement}
				>
					<AriaButton
						{...buttonProps}
						onClick={(e) =>
							handleCopy(e as unknown as React.MouseEvent<HTMLButtonElement>)
						}
						className={cx(
							"inline-flex items-center justify-center rounded p-1 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2",
							iconClassName,
						)}
						aria-label={tooltipContent}
					>
						<Icon
							size={iconSize}
							className={cx(
								"transition-colors",
								isCopied ? "text-success-600" : "text-gray-400",
							)}
						/>
					</AriaButton>
				</Tooltip>
			)}
		</div>
	);
}
